import { signOut } from "firebase/auth";
import { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link, useNavigate } from "react-router-dom";
import auth from "../../firebase.init";
import axios from "axios";

const NavBar = () => {
  const [user] = useAuthState(auth);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navData, setNavData] = useState(null);

  const CACHE_KEYS = {
    AUTH_STATE: "app_auth_state",
    USER_DATA: "app_user_data",
  };

  // --- FETCH DYNAMIC NAVBAR DATA ---
  useEffect(() => {
    const fetchNavData = async () => {
      try {
        const response = await axios.get("https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/api/navbar-v5");
        setNavData(response.data.data);
      } catch (err) {
        setError("Failed to load navbar data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchNavData();
  }, []);

  // HANDLE AUTH STATE & USER DATA (Existing Logic)
  useEffect(() => {
    if (!loading) {
      try {
        const authStateData = {
          hasUser: !!user,
          timestamp: Date.now(),
          userEmail: user?.email || null,
        };
        localStorage.setItem(CACHE_KEYS.AUTH_STATE, JSON.stringify(authStateData));
      } catch (error) {
        console.error("Error caching auth state:", error);
      }
    }
  }, [user, loading]);

  const handleSignout = async () => {
    try {
      await signOut(auth);
      Object.values(CACHE_KEYS).forEach((key) => {
        try { localStorage.removeItem(key); } catch (e) { console.error(e); }
      });
      setUserData(null);
      setMobileMenuVisible(false);
      navigate("/");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const toggleMobileMenu = () => setMobileMenuVisible(!mobileMenuVisible);

  // Fetch user role data (Existing Logic)
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.email) {
        setUserData(null);
        try { localStorage.removeItem(CACHE_KEYS.USER_DATA); } catch (e) {}
        return;
      }
      try {
        const response = await fetch(`https://all-in-one-youtube-cc51e2f64b53.herokuapp.com/users?userEmail=${user.email}`);
        const data = await response.json();
        if (data && data.length > 0) {
          setUserData(data[0]);
          localStorage.setItem(CACHE_KEYS.USER_DATA, JSON.stringify(data[0]));
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };

    if (user?.email) {
      const cached = localStorage.getItem(CACHE_KEYS.USER_DATA);
      if (!cached || !userData) fetchUserData();
    }
  }, [user]);

  // Determine Dashboard Text/Link based on Role
  const getDashboardConfig = () => {
    if (userData?.userRole === "Admin") {
      return { link: "/admin/dashboard", text: "Admin Dashboard" };
    }
    return { link: "/user/dashboard", text: navData?.authLabels?.dashboard || "Dashboard" };
  };

  const dashboardConfig = getDashboardConfig();

  if (loading) return <div className="container text-center py-2"></div>;
  if (error || !navData || !navData.isActive) return null;

  return (
    <>
      <header className="header-section">
        <div className="container">
          <div className="header-holder d-flex flex-wrap justify-content-between align-items-center">
            {/* Dynamic Logo */}
            <div className="brand-logo d-none d-lg-inline-block">
              <div className="logo">
                <Link to="/">
                  <img src={navData.logo || "/assets/images/logo/logo.png"} alt="logo" />
                </Link>
              </div>
            </div>
            
            <div className="header-menu-part">
              {/* Dynamic Header Top (Contact & Socials) */}
              <div className="header-top">
                <div className="header-top-area">
                  <ul className="left">
                    <li>
                      <i className="icofont-ui-call" />
                      <span>{navData.contact?.phone || ""}</span>
                    </li>
                    <li>
                      <i className="icofont-location-pin" /> {navData.contact?.address || ""}
                    </li>
                  </ul>
                  <ul className="social-icons d-flex align-items-center">
                    {navData.socials?.map((social, index) => (
                      <li key={index}>
                        <a href={social.url} className={social.styleClass}>
                          <i className={social.iconClass} />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="header-bottom">
                <div className="header-wrapper justify-content-lg-end">
                  <div className="mobile-logo d-lg-none">
                    <Link to="/">
                      <img src={navData.logo || "/assets/images/logo/logo.png"} alt="logo" />
                    </Link>
                  </div>
                  
                  <div className="menu-area">
                    <ul className={`menu ${mobileMenuVisible ? "active" : ""}`}>
                      {/* Dynamic Menu Items */}
                      {navData.menuItems?.map((item, index) => (
                        <li key={index}>
                          <Link to={item.link} className={index === 0 ? "active" : ""}>
                            {item.label}
                          </Link>
                        </li>
                      ))}

                      {/* Mobile Auth Links */}
                      {!user ? (
                        <>
                          <li className="d-lg-none"><Link to="/login">{navData.authLabels?.login || "Login"}</Link></li>
                          <li className="d-lg-none"><Link to="/register">{navData.authLabels?.signup || "Sign Up"}</Link></li>
                        </>
                      ) : (
                        <>
                          <li className="d-lg-none"><Link to={dashboardConfig.link}>{dashboardConfig.text}</Link></li>
                          <li className="d-lg-none">
                            <button onClick={handleSignout} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }}>
                              {navData.authLabels?.logout || "Logout"}
                            </button>
                          </li>
                        </>
                      )}
                    </ul>

                    {/* Desktop Auth Buttons */}
                    {!user ? (
                      <>
                        <Link to="/login" className="login d-none d-lg-inline-flex">
                          <i className="icofont-user" /> <span>{navData.authLabels?.login || "LOG IN"}</span>
                        </Link>
                        <Link to="/register" className="signup d-none d-lg-inline-flex">
                          <i className="icofont-users" /> <span>{navData.authLabels?.signup || "SIGN UP"}</span>
                        </Link>
                      </>
                    ) : (
                      <>
                        <Link to={dashboardConfig.link} className="login d-none d-lg-inline-flex">
                          <i className="icofont-dashboard" /> <span>{dashboardConfig.text}</span>
                        </Link>
                        <button 
                          onClick={handleSignout}
                          className="signup d-none d-lg-inline-flex"
                          style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
                        >
                           <i className="icofont-logout" /> <span>{navData.authLabels?.logout || "LOGOUT"}</span>
                        </button>
                      </>
                    )}

                    {/* Toggle Icons */}
                    <div className="header-bar d-lg-none" onClick={toggleMobileMenu} style={{ cursor: "pointer" }}>
                      <span /><span /><span />
                    </div>
                    <div className="ellepsis-bar d-lg-none">
                      <i className="icofont-info-square" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default NavBar;